import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  HiOutlineRefresh, HiOutlineCheckCircle, HiOutlineXCircle,
  HiOutlinePause, HiOutlineViewList, HiOutlineSearch,
  HiOutlineChevronDown, HiOutlineChevronRight, HiOutlineDownload,
} from 'react-icons/hi';
import { getTaskQueues, getServiceTags, setServiceTag } from '../services/api';
import ServiceTagDropdown, { groupAndSort, getTagLabel, TagFilter, stickyTh, stickyTd, stickyGroupTd } from '../components/ServiceTagDropdown';
import './CloudScheduler.css';

const SERVICE_TYPE = 'cloud-tasks';

function formatCost() {
  return <span className="cost-cell"><span className="cost-approx">~</span> ₹0</span>;
}

function queueStateLabel(state) {
  if (state === 'RUNNING' || state === 1) return 'RUNNING';
  if (state === 'PAUSED' || state === 2) return 'PAUSED';
  if (state === 'DISABLED' || state === 3) return 'DISABLED';
  return String(state || 'UNKNOWN');
}

function queueStateIcon(state) {
  const label = queueStateLabel(state);
  if (label === 'RUNNING') return <HiOutlineCheckCircle size={14} className="state-icon enabled" />;
  if (label === 'PAUSED') return <HiOutlinePause size={14} className="state-icon paused" />;
  if (label === 'DISABLED') return <HiOutlineXCircle size={14} className="state-icon disabled" />;
  return null;
}

function escapeCsvField(val) {
  const str = String(val ?? '');
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function CloudTasks({ gcpProjects, selectedProjectId }) {
  const [queues, setQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [tagMap, setTagMap] = useState({});
  const [tagFilter, setTagFilter] = useState('all');

  const activeProject = gcpProjects.find(p => p._id === selectedProjectId);

  const fetchQueues = useCallback(async (signal) => {
    if (!activeProject) return;
    setLoading(true);
    try {
      const res = await getTaskQueues(activeProject._id);
      if (!signal?.cancelled) setQueues(res.data || []);
    } catch (err) {
      if (!signal?.cancelled) {
        toast.error('Failed to load task queues: ' + (err.response?.data?.error || err.message));
        setQueues([]);
      }
    } finally {
      if (!signal?.cancelled) setLoading(false);
    }
  }, [activeProject]);

  const fetchTags = useCallback(async () => {
    if (!activeProject) return;
    try {
      const res = await getServiceTags(activeProject._id);
      const map = {};
      for (const [key, val] of Object.entries(res.data || {})) {
        if (key.startsWith(SERVICE_TYPE + ':')) {
          map[key.replace(SERVICE_TYPE + ':', '')] = val;
        }
      }
      setTagMap(map);
    } catch { /* tags are optional */ }
  }, [activeProject]);

  useEffect(() => {
    setQueues([]);
    setExpandedRows({});
    setTagMap({});
    if (!activeProject) return;
    const signal = { cancelled: false };
    fetchQueues(signal);
    fetchTags();
    return () => { signal.cancelled = true; };
  }, [activeProject, fetchQueues, fetchTags]);

  const handleTagChange = async (serviceName, tag) => {
    const prev = { ...tagMap };
    setTagMap(m => tag ? { ...m, [serviceName]: tag } : (() => { const n = { ...m }; delete n[serviceName]; return n; })());
    try {
      await setServiceTag(activeProject._id, SERVICE_TYPE, serviceName, tag);
    } catch {
      setTagMap(prev);
      toast.error('Failed to update tag');
    }
  };

  const sortFn = useCallback((list) => {
    return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, []);

  const grouped = useMemo(() => {
    let list = queues;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(queue =>
        queue.name.toLowerCase().includes(q) ||
        queue.region.toLowerCase().includes(q) ||
        queueStateLabel(queue.state).toLowerCase().includes(q)
      );
    }
    return groupAndSort(list, tagMap, 'name', sortFn, tagFilter);
  }, [queues, search, tagMap, sortFn, tagFilter]);

  const flatFiltered = useMemo(() => grouped.filter(r => !r._groupHeader), [grouped]);

  const toggleRow = (key) => setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));

  const COL_COUNT = 10;

  const downloadCSV = () => {
    if (flatFiltered.length === 0) return;
    const headers = ['Tag', 'Queue Name', 'Region', 'State', 'Est. Cost (INR)', 'Max Dispatches/s', 'Max Concurrent', 'Max Attempts', 'Last Purge'];
    const rows = flatFiltered.map(q => [
      escapeCsvField(getTagLabel(tagMap[q.name]) || ''),
      escapeCsvField(q.name),
      escapeCsvField(q.region),
      escapeCsvField(queueStateLabel(q.state)),
      escapeCsvField(0),
      escapeCsvField(q.rateLimits?.maxDispatchesPerSecond ?? ''),
      escapeCsvField(q.rateLimits?.maxConcurrentDispatches ?? ''),
      escapeCsvField(q.retryConfig?.maxAttempts ?? ''),
      escapeCsvField(q.purgeTime ? new Date(q.purgeTime).toLocaleString() : ''),
    ]);

    const csvContent = [
      headers.map(escapeCsvField).join(','),
      ...rows.map(r => r.join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cloud-tasks-queues-${activeProject?.projectId || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!activeProject) {
    return (
      <div className="cloud-scheduler page-container">
        <div className="gcp-page-header">
          <div className="gcp-page-title-row"><h1><HiOutlineViewList size={28} /> Cloud Tasks</h1></div>
        </div>
        <div className="empty-state"><p>Select a GCP project to view Cloud Task queues.</p></div>
      </div>
    );
  }

  return (
    <div className="cloud-scheduler page-container">
      <div className="gcp-page-header">
        <div className="gcp-page-title-row">
          <h1><HiOutlineViewList size={28} /> Cloud Tasks</h1>
          <span className="pv-subtitle">{activeProject.projectName} — {flatFiltered.length} queue{flatFiltered.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="gcp-page-toolbar">
          <div className="header-search">
            <HiOutlineSearch size={14} />
            <input type="text" placeholder="Search queues..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <TagFilter value={tagFilter} onChange={setTagFilter} />
          <button className="btn btn-ghost btn-sm" onClick={downloadCSV} disabled={loading || flatFiltered.length === 0} title="Download CSV">
            <HiOutlineDownload size={14} /> CSV
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => fetchQueues({})} disabled={loading}>
            <HiOutlineRefresh size={14} className={loading ? 'spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {loading && <div className="loading-bar"><div className="loading-bar-inner" /></div>}

      {!loading && queues.length === 0 && (
        <div className="empty-state"><p>No task queues found in <strong>{activeProject.projectName}</strong>.</p></div>
      )}

      {grouped.length > 0 && (
        <div className="resource-table-wrap">
          <div className="resource-table-scroll">
          <table className="resource-table">
            <thead>
              <tr>
                <th style={stickyTh[0]}></th>
                <th style={stickyTh[1]}>Tag</th>
                <th style={stickyTh[2]}>Queue Name</th>
                <th>Region</th>
                <th>State</th>
                <th>Est. Cost</th>
                <th>Max Dispatches/s</th>
                <th>Max Concurrent</th>
                <th>Max Attempts</th>
                <th>Last Purge</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map((row, i) => {
                if (row._groupHeader) {
                  return (
                    <tr key={`gh-${row.group}`} className="group-header-row">
                      <td style={stickyGroupTd[0]}></td>
                      <td style={stickyGroupTd[1]}></td>
                      <td style={stickyGroupTd[2]}>
                        <span className="group-label">
                          <span className={`group-dot ${row.group}`}></span>
                          {row.label}
                          <span className="group-count">({row.count})</span>
                        </span>
                      </td>
                      <td colSpan={COL_COUNT - 3}></td>
                    </tr>
                  );
                }
                const q = row;
                const key = q.fullName || i;
                const isOpen = expandedRows[key];
                return (
                  <React.Fragment key={key}>
                    <tr className="expandable-row" onClick={() => toggleRow(key)}>
                      <td className="expand-toggle" style={stickyTd[0]}>
                        {isOpen ? <HiOutlineChevronDown size={14} /> : <HiOutlineChevronRight size={14} />}
                      </td>
                      <td style={stickyTd[1]}>
                        <ServiceTagDropdown value={tagMap[q.name] || ''} onChange={(tag) => handleTagChange(q.name, tag)} />
                      </td>
                      <td className="cell-name" style={stickyTd[2]}>{q.name}</td>
                      <td><span className="region-badge">{q.region}</span></td>
                      <td>
                        <span className={`status-badge ${queueStateLabel(q.state) === 'RUNNING' ? 'success' : queueStateLabel(q.state) === 'PAUSED' ? 'warning' : 'error'}`}>
                          {queueStateIcon(q.state)} {queueStateLabel(q.state)}
                        </span>
                      </td>
                      <td>{formatCost()}</td>
                      <td>{q.rateLimits?.maxDispatchesPerSecond ?? '—'}</td>
                      <td>{q.rateLimits?.maxConcurrentDispatches ?? '—'}</td>
                      <td>{q.retryConfig?.maxAttempts ?? '—'}</td>
                      <td>{q.purgeTime ? new Date(q.purgeTime).toLocaleString() : '—'}</td>
                    </tr>
                    {isOpen && (
                      <tr className="expand-detail-row">
                        <td colSpan={COL_COUNT}>
                          <div className="expand-detail">
                            <div className="detail-grid">
                              <div className="detail-item full-width">
                                <span className="detail-label">Full Name</span>
                                <span className="detail-value mono">{q.fullName || '—'}</span>
                              </div>
                              <div className="detail-row">
                                <div className="detail-item">
                                  <span className="detail-label">State</span>
                                  <span className="detail-value">{queueStateLabel(q.state)}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Region</span>
                                  <span className="detail-value">{q.region}</span>
                                </div>
                              </div>
                              <div className="detail-divider">Rate Limits</div>
                              <div className="detail-row">
                                <div className="detail-item">
                                  <span className="detail-label">Max Dispatches/s</span>
                                  <span className="detail-value">{q.rateLimits?.maxDispatchesPerSecond ?? '—'}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Max Concurrent</span>
                                  <span className="detail-value">{q.rateLimits?.maxConcurrentDispatches ?? '—'}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Max Burst Size</span>
                                  <span className="detail-value">{q.rateLimits?.maxBurstSize ?? '—'}</span>
                                </div>
                              </div>
                              <div className="detail-divider">Retry Config</div>
                              <div className="detail-row">
                                <div className="detail-item">
                                  <span className="detail-label">Max Attempts</span>
                                  <span className="detail-value">{q.retryConfig?.maxAttempts ?? '—'}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Max Retry Duration</span>
                                  <span className="detail-value">{q.retryConfig?.maxRetryDuration || '—'}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Min Backoff</span>
                                  <span className="detail-value">{q.retryConfig?.minBackoff || '—'}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Max Backoff</span>
                                  <span className="detail-value">{q.retryConfig?.maxBackoff || '—'}</span>
                                </div>
                              </div>
                              {q.purgeTime && (
                                <div className="detail-item">
                                  <span className="detail-label">Last Purge</span>
                                  <span className="detail-value">{new Date(q.purgeTime).toLocaleString()}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {!loading && queues.length > 0 && flatFiltered.length === 0 && (
        <div className="empty-state"><p>No queues match "<strong>{search}</strong>"</p></div>
      )}
    </div>
  );
}

export default CloudTasks;
